export const SLOT_MINUTES = 30;

/** Pixels per 30 minutes. The classic view runs time down, the resource board runs it across. */
export const CLASSIC_SLOT_HEIGHT = 90;

/**
 * A one-unit booking gets a single lane, so the lane height IS the block height for the most
 * common case, and a 30-minute booking — the shortest service — is one slot wide. Both are
 * sized so that block shows everything with nothing dropped or cut: the status chip with its
 * longest label ("Jóváhagyásra vár"), a name and a service name of up to two lines each, and
 * the time range. The owner's call (2026-09-11): make the grid big enough rather than trim
 * the content. At 144 × 68 a narrow block lost its chip label and its time row; at 46px tall
 * everything had truncated.
 */
export const RESOURCE_SLOT_WIDTH = 160;
export const RESOURCE_LANE_HEIGHT = 116;

/** Width of the sticky lane-label column; it overlaps the track, so scrolling must clear it. */
export const RESOURCE_GUTTER_WIDTH = 148;

/** Collapsed, the lane column keeps only the index — worth the room on a phone. */
export const RESOURCE_GUTTER_COLLAPSED_WIDTH = 44;

export const timeToMinutes = (time) => {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
};

export const minutesToTime = (minutes) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

export const DAY_MINUTES = 24 * 60;

/**
 * The span of the day the grid draws, from the tenant's week: one entry per open weekday with
 * that day's hours (D33). With `showOutsideHours` it is the **whole day**, those hours only
 * marking which part is business time; without it — the dashboard's default,
 * [DECISIONS.md](../../../../docs/DECISIONS.md) D24 — it runs from the week's earliest opening to
 * its latest closing, so a Friday running to 22:00 is drawable beside a Thursday closing at
 * 19:00.
 *
 * A grid clipped to opening hours can still have bookings outside it: an admin may book outside
 * opening hours on purpose (D2), and `manualBooking` and `blockPeriod` skip the business-hours
 * check. Positioned as they were, such bookings once landed at a negative offset and rendered
 * outside their column, so every booking goes through `clipToWindow` before it is drawn.
 *
 * Hours that do not make one span — closing at or before opening — keep the whole day.
 */
export const buildGridWindow = ({ showOutsideHours, dailyBreaks = [], openingHours = [] }) => {
  const byWeekday = Object.fromEntries(
    openingHours.map((day) => [
      day.dayOfWeek,
      { start: timeToMinutes(day.openingTime), end: timeToMinutes(day.closingTime) },
    ])
  );
  const week = Object.values(byWeekday);

  // The window spans the whole week, so one grid can carry a day closing at 19:00 beside one
  // closing at 22:00. Each day is shaded by its own hours, which is what `isBusinessMinute`
  // answers. With no week at all — the tenant is still loading, or an older backend — the grid
  // falls back to a plain 8–20 rather than drawing nothing.
  const businessStart = week.length ? Math.min(...week.map((day) => day.start)) : 8 * 60;
  const businessEnd = week.length ? Math.max(...week.map((day) => day.end)) : 20 * 60;
  const clipped = !showOutsideHours && businessEnd > businessStart;
  const start = clipped ? businessStart : 0;
  const end = clipped ? businessEnd : DAY_MINUTES;

  return {
    start,
    end,
    slotCount: (end - start) / SLOT_MINUTES,
    businessStart,
    businessEnd,
    byWeekday,
    dailyBreaks,
  };
};

/**
 * The part of a booking inside the window, in minutes, or `null` when none of it is. A booking
 * that crosses an edge is drawn up to that edge; its label keeps the real times.
 */
export const clipToWindow = (window, start, end) => {
  const clippedStart = Math.max(start, window.start);
  const clippedEnd = Math.min(end, window.end);
  return clippedEnd > clippedStart ? { start: clippedStart, end: clippedEnd } : null;
};

export const isSlotInWindow = (window, slot) =>
  clipToWindow(window, timeToMinutes(slot.startTime), timeToMinutes(slot.endTime)) !== null;

/**
 * Is this minute business time? With a date, on that very day, by its own weekday hours
 * ([DECISIONS.md](../../../../docs/DECISIONS.md) D33) — a weekday the tenant is closed on has no
 * hours at all, so nothing on it is business time. Without a date — the shared time ruler, which
 * stands beside every column — the whole week's span answers, because a single ruler cannot be
 * right for six days at once.
 */
export const isBusinessMinute = (window, minute, isoDate) => {
  if (isoDate && Object.keys(window.byWeekday || {}).length) {
    const day = window.byWeekday[weekdayOf(isoDate)];
    return day ? minute >= day.start && minute < day.end : false;
  }

  return minute >= window.businessStart && minute < window.businessEnd;
};

/** Backend weekday names, indexed as `Date.getDay()` numbers them. */
const WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

/**
 * The weekday of a `YYYY-MM-DD` date, named as the backend names it. Parsed part by part on
 * purpose: `new Date('2026-09-17')` is midnight **UTC**, which is the day before in any negative
 * offset, and the schedules' dates are local calendar dates.
 */
export const weekdayOf = (isoDate) => {
  const [year, month, day] = isoDate.split('-').map(Number);
  return WEEKDAYS[new Date(year, month - 1, day).getDay()];
};

/**
 * The tenant's daily breaks on one day, as minute bands clipped to the window
 * ([DECISIONS.md](../../../../docs/DECISIONS.md) D32). A break without a weekday is on every day
 * the tenant is open. The bands are shading only: the backend leaves the guest's slots out, while
 * an admin may still book into one, as they may outside opening hours (D2).
 */
export const breakBands = (window, isoDate) => {
  // A closed day has no business time to take a break from: the whole column is closed already.
  if (Object.keys(window.byWeekday || {}).length && !window.byWeekday[weekdayOf(isoDate)]) {
    return [];
  }

  return (window.dailyBreaks || [])
    .filter((dailyBreak) => !dailyBreak.dayOfWeek || dailyBreak.dayOfWeek === weekdayOf(isoDate))
    .map((dailyBreak) =>
      clipToWindow(window, timeToMinutes(dailyBreak.startTime), timeToMinutes(dailyBreak.endTime))
    )
    .filter(Boolean);
};

export const isInBands = (bands, minute) =>
  bands.some((band) => minute >= band.start && minute < band.end);

/** Diagonal hatching, so a break reads as a different thing from the flat grey of closed hours. */
export const BREAK_HATCHING =
  'repeating-linear-gradient(45deg, #e8e8ee 0px, #e8e8ee 5px, #f5f5f8 5px, #f5f5f8 11px)';

/**
 * `toISOString()` is UTC, so late in the evening it names tomorrow. Every "is this today?"
 * comparison here is against a local calendar date.
 */
export const localIsoDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const minutesOfDayNow = (now = new Date()) => now.getHours() * 60 + now.getMinutes();

/** Start minutes of every booking on one fetched day, ascending. */
export const bookingStartMinutes = (dailySchedule) =>
  (dailySchedule?.timeSlots || [])
    .map((slot) => timeToMinutes(slot.startTime))
    .sort((a, b) => a - b);

/**
 * Minute-of-day of the earliest booking still ahead of now, across every fetched day. Only the
 * column board uses it: it shows several days side by side, so tomorrow's first booking really
 * is on screen and scrolling to its time-of-day helps. The resource board shows one day, where
 * that booking would be nowhere to be seen.
 */
export const nextBookingMinuteOfDay = (dailySchedules = [], now = new Date()) => {
  const todayIso = localIsoDate(now);
  const nowMinutes = minutesOfDayNow(now);
  let best = null;

  dailySchedules.forEach((schedule) => {
    (schedule.timeSlots || []).forEach((slot) => {
      const minute = timeToMinutes(slot.startTime);
      const isAhead =
        schedule.date > todayIso || (schedule.date === todayIso && minute >= nowMinutes);
      if (!isAhead) return;

      const key = `${schedule.date} ${String(minute).padStart(4, '0')}`;
      if (best === null || key < best.key) best = { key, minute };
    });
  });

  return best ? best.minute : null;
};

/**
 * Where the board opens. The full rule set, and why the two boards differ in the last case, is
 * DECISIONS.md D15 — read that before changing any branch here.
 *
 *   shown day is not today  → that day's first booking, else opening time
 *   now < opening           → today's first booking, else opening time
 *   opening ≤ now < closing → now's slot
 *   now ≥ closing, bookings still to come today → now's slot
 *   now ≥ closing, nothing left today → column board: next booking anywhere in the fetched
 *                                        days (else opening); resource board: now's slot
 *
 * "Now's slot" is the start of the grid slot the current minute falls in — 14:47 opens on
 * 14:30 — so the slot in progress is whole at the leading edge instead of cut through. Only
 * the landing point is rounded; every comparison above is against the exact minute.
 *
 * The bookings are the ones on the grid: `DashboardPage` passes only those inside the window, so
 * with the hours outside opening time hidden, a booking there is not a landing point (D24).
 */
export const resolveInitialScrollMinute = ({
  window,
  dayStartMinutes = [],
  isShownDayToday,
  nextBookingMinute = null,
  fallBackToNextBooking = false,
  now = new Date(),
}) => {
  const firstBookingOfDay = dayStartMinutes.length ? dayStartMinutes[0] : null;

  // No meaningful "now" on a day that is not today. Also covers a closed day, where the opening
  // hour is the least surprising landing point even though the tenant is shut.
  if (!isShownDayToday) return firstBookingOfDay ?? window.businessStart;

  const nowMinutes = minutesOfDayNow(now);
  const nowSlotStart = Math.floor(nowMinutes / SLOT_MINUTES) * SLOT_MINUTES;

  if (nowMinutes < window.businessStart) return firstBookingOfDay ?? window.businessStart;
  if (nowMinutes < window.businessEnd) return nowSlotStart;

  const hasBookingsLeftToday = dayStartMinutes.some((minute) => minute >= nowMinutes);
  if (hasBookingsLeftToday || !fallBackToNextBooking) return nowSlotStart;

  return nextBookingMinute ?? window.businessStart;
};

/**
 * Scroll offset that puts `minute` at the leading edge of the track, for either axis.
 *
 * The sticky ruler / lane gutter overlays the start of the viewport, but the track's own
 * coordinates already begin after it, so its width must **not** be subtracted here — doing that
 * pushes the target a gutter's width further along. Getting this wrong is what put 12:00 two
 * slots into the view when the board was supposed to open on it.
 *
 * The offset counts from the window's start. A minute before the window lands on its start; one
 * past its end asks for more than the board can scroll, and the browser stops at the end.
 */
export const scrollOffsetForMinute = (minute, pixelsPerSlot, window) =>
  Math.max(((minute - window.start) / SLOT_MINUTES) * pixelsPerSlot, 0);

/** The minute at the leading edge for a scroll offset — the inverse of `scrollOffsetForMinute`. */
export const minuteForScrollOffset = (offset, pixelsPerSlot, window) =>
  window.start + (offset / pixelsPerSlot) * SLOT_MINUTES;

export const slotStarts = ({ start, slotCount }) =>
  Array.from({ length: slotCount }, (_, i) => start + i * SLOT_MINUTES);

export const coversMinute = (slot, minute) =>
  timeToMinutes(slot.startTime) <= minute && timeToMinutes(slot.endTime) > minute;

/** How many units of one resource type this booked slot consumes. 0 when it uses none. */
export const demandFor = (slot, resourceTypeId) => {
  const usage = (slot.resourceUsages || []).find((u) => u.resourceTypeId === resourceTypeId);
  return usage ? usage.quantity : 0;
};

export const usedCapacityAt = (slots, resourceTypeId, minute) =>
  slots.reduce(
    (sum, slot) => (coversMinute(slot, minute) ? sum + demandFor(slot, resourceTypeId) : sum),
    0
  );

/**
 * A booking with no supply (a BLOCKED period) closes the whole site, so it consumes every unit
 * of every pool for its interval. The backend treats it the same way in the conflict check.
 */
export const isBlockingSlot = (slot) => slot.bookingStatus === 'BLOCKED';

export const isSlotBlockedAt = (slots, minute) =>
  slots.some((slot) => isBlockingSlot(slot) && coversMinute(slot, minute));

/**
 * Today's and tomorrow's guests who are not wholly inside opening hours, for the notice by the
 * dashboard's toggle: with those hours hidden, such a booking is off the grid. A blocked period is
 * not a guest. Today counts only what has not ended yet — a guest who came at 07:00 is no news at
 * 15:00. Returns `[{ isToday, ranges: ['07:00–07:30', …] }]`, today first; a day with none is left out.
 */
export const upcomingOutsideHoursGuests = (dailySchedules, window, now = new Date()) => {
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const nowMinutes = minutesOfDayNow(now);

  return [
    { isToday: true, date: localIsoDate(now) },
    { isToday: false, date: localIsoDate(tomorrow) },
  ]
    .map(({ isToday, date }) => {
      const schedule = dailySchedules.find((candidate) => candidate.date === date);
      const ranges = (schedule?.timeSlots || [])
        .map((slot) => ({ slot, start: timeToMinutes(slot.startTime), end: timeToMinutes(slot.endTime) }))
        .filter(({ slot, start, end }) =>
          !isBlockingSlot(slot) &&
          // Against that day's own hours (D33): 20:00 is business time on a long day and not on
          // a short one. A minute of the booking outside them is what makes it a notice.
          (!isBusinessMinute(window, start, date) || !isBusinessMinute(window, end - 1, date)) &&
          (!isToday || end > nowMinutes))
        .sort((a, b) => a.start - b.start)
        .map(({ start, end }) => `${minutesToTime(start)}–${minutesToTime(end)}`);

      return { isToday, ranges: [...new Set(ranges)] };
    })
    .filter(({ ranges }) => ranges.length > 0);
};

/** Ascending lane indexes as runs of adjacent lanes: [0, 2, 3] → [{first: 0, count: 1}, {first: 2, count: 2}]. */
export const laneRuns = (lanes) =>
  lanes.reduce((runs, lane) => {
    const last = runs[runs.length - 1];
    if (last && last.first + last.count === lane) last.count += 1;
    else runs.push({ first: lane, count: 1 });
    return runs;
  }, []);

/**
 * `demand` of the free lanes, in as few runs as possible: the first run long enough when there
 * is one, otherwise the longest runs first (top-down among equals).
 */
const pickLanes = (freeLanes, demand) => {
  const runs = laneRuns(freeLanes);
  const fitting = runs.find((run) => run.count >= demand);
  if (fitting) return Array.from({ length: demand }, (_, k) => fitting.first + k);

  const lanes = [];
  [...runs]
    .sort((a, b) => b.count - a.count)
    .forEach((run) => {
      for (let k = 0; k < run.count && lanes.length < demand; k++) lanes.push(run.first + k);
    });
  return lanes.sort((a, b) => a - b);
};

/**
 * Greedy first-fit in start order. A booking holds its lanes for its whole span; they are
 * adjacent when enough adjacent lanes are free, and otherwise `runs` splits them into the
 * pieces the board draws one block each.
 *
 * Adjacency is only a preference because the free *count* is exact but its shape is not: at
 * 12:00 bookings for 1, 1 and 2 headsets, the second running to 13:00 in lane 2, leave lanes 1,
 * 3 and 4 for a 3-headset booking at 12:30. Insisting on adjacency marked that booking over
 * capacity and stacked it on top of lane 2.
 *
 * Placing in start order means every placed booking that overlaps a new one is still running
 * at its start, so the lanes free for the whole span are the lanes not busy at that instant —
 * and the server's capacity check leaves at least `demand` of those. `overflow` is therefore
 * real over-capacity data, never a packing artefact.
 *
 * This is a DISPLAY packing. The backend stores a unit count, not unit identity, so lane 2 does
 * not mean "the second headset" — see DECISIONS.md D13 for the model change that would make
 * these lanes real, at which point pinned bookings claim their lane before this runs.
 */
export const packLanes = (slots, resourceTypeId, capacity) => {
  const busy = Array.from({ length: capacity }, () => []);
  const allLanes = Array.from({ length: capacity }, (_, lane) => lane);
  // A blocked period has no supply and so no demand of its own, but it closes every unit of every
  // pool (isBlockingSlot), so it is placed across all of them. Left out, it was on no lane at all:
  // invisible on the board, and so impossible to open and lift from there.
  const demandOf = (slot) => (isBlockingSlot(slot) ? capacity : demandFor(slot, resourceTypeId));

  const relevant = slots
    .filter((slot) => demandOf(slot) > 0)
    .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

  const placed = [];

  relevant.forEach((slot) => {
    const start = timeToMinutes(slot.startTime);
    const end = timeToMinutes(slot.endTime);
    const demand = demandOf(slot);

    const isFree = (lane) => busy[lane].every(([busyStart, busyEnd]) => busyStart >= end || busyEnd <= start);
    const freeLanes = allLanes.filter(isFree);
    const overflow = freeLanes.length < demand;

    // Over capacity should be impossible — the server rejects it — but never drop a booking off
    // the grid because of it. Take every free lane, top up from the first busy ones and let it
    // overlap visibly.
    const lanes = overflow
      ? [...freeLanes, ...allLanes.filter((lane) => !isFree(lane)).slice(0, Math.min(demand, capacity) - freeLanes.length)]
          .sort((a, b) => a - b)
      : pickLanes(freeLanes, demand);

    lanes.forEach((lane) => busy[lane].push([start, end]));
    placed.push({ slot, lanes, runs: laneRuns(lanes), start, end, demand, overflow });
  });

  return placed;
};
